import { Button, CircularProgress, DialogActions, Divider } from '@mui/material';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import { Field, FieldProps, Form, Formik } from 'formik';
import { Field as UIField } from '@globalvisioninc/ui/field';
import { useDispatchRequest } from '@redux-requests/react';
import { VerifyDialog } from 'components/common';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVSelect from 'components/lib/GVSelect/GVSelect';
import { SELECT_EMPTY_VALUE } from '@globalvisioninc/ui/select';
import {
  createOrganization,
  setOrganizationScimManaged,
  updateOrganization,
  errors as responseErrors,
} from 'store/myAccount/requests';
import { Organization, OrganizationModalData } from 'types';
import DatePickerField from './DatePickerField';
import { useConfirmation } from 'utils';
import styles from './OrganizationModal.module.css';

interface OrganizationModalValidationData extends OrganizationModalData {
  domainInUse?: boolean;
}

// take the TenantMemberData interface and type them all to strings
type OrganizationErrorType = Omit<OrganizationModalData, 'seatsLimit' | 'expiry'> & {
  seatsLimit: string;
  expiry: string;
};

interface OrganizationModalProps {
  title: string;
  subtitle: string;
  iconType: string;
  open: boolean;
  orgInfo?: Organization;
  edit?: boolean;
  canManageScim?: boolean;
  onClose: () => void;
}

const RETENTION_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'None' },
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 120, label: '120 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
];

const OrganizationModal = ({
  title,
  subtitle,
  open,
  edit,
  canManageScim,
  onClose,
  iconType,
  orgInfo,
}: OrganizationModalProps) => {
  const dispatchRequest = useDispatchRequest();
  const confirm = useConfirmation();
  const orgId = orgInfo?.id;
  const today = new Date();
  const expiryDate = (orgInfo?.expiry && new Date(orgInfo.expiry)) || today;

  const validateForm = (values: OrganizationModalValidationData) => {
    const errors: Partial<OrganizationErrorType> = {};
    const urlRegex =
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
    if (!values.name.trim()) {
      errors.name = 'Required field';
    }
    if (!values.customerId) {
      errors.customerId = 'Required field';
    } else if (values.customerId.length < 6) {
      errors.customerId = 'Must be 6 digits';
    }
    if (values.veevaUrl && !urlRegex.test(values.veevaUrl)) {
      errors.veevaUrl = 'Must be a valid URL';
    }
    if (!values.adminFirstName.trim()) {
      errors.adminFirstName = 'Required field';
    }
    if (!values.adminLastName.trim()) {
      errors.adminLastName = 'Required field';
    }
    if (!values.adminEmail.trim()) {
      errors.adminEmail = 'Required field';
    } else if (!emailRegex.test(values.adminEmail)) {
      errors.adminEmail = 'Invalid email address';
    }

    if (values.ssoEnabled && !values.domain?.trim()) {
      errors.domain = 'Required field';
    }

    if (values.domainInUse) {
      errors.domain = responseErrors.DOMAIN_ALREADY_EXISTS;
    }
    if (!values.seatsLimit) {
      errors.seatsLimit = 'Required field';
    } else if (values.seatsLimit < 0) {
      errors.seatsLimit = 'Invalid number of seats';
    }
    if (!values.expiry) {
      errors.expiry = 'Required field';
    }
    // TODO validate unparsible dates (ex. 99/99/999) and past dates
    return errors;
  };

  const initialValues: OrganizationModalData = {
    name: orgInfo?.name || '',
    adminFirstName: orgInfo?.adminFirstName || '',
    adminLastName: orgInfo?.adminLastName || '',
    adminEmail: orgInfo?.adminEmail || '',
    seatsLimit: orgInfo?.seatsLimit || 1,
    // We have console error in the date picker for some reason, not crash but we need to investigate !
    expiry: expiryDate,
    customerId: orgInfo?.customerId || '',
    veevaUrl: orgInfo?.veevaUrl,
    eskoIntegration: orgInfo?.eskoIntegration || false,
    ssoEnabled: orgInfo?.ssoEnabled || false,
    domain: orgInfo?.domain || '',
    hardSeatEnabled: orgInfo?.hardSeatEnabled || false,
    slowTrackCustomer: orgInfo?.slowTrackCustomer || false,
    disableAiUsage: orgInfo?.disableAiUsage || false,
    retentionDays: orgInfo?.retentionDays ?? null,
    scimManaged: orgInfo?.scimManaged === true,
  };

  const handleSubmit = async (values: OrganizationModalData, actions: any) => {
    if (initialValues.retentionDays === null && values.retentionDays !== null) {
      try {
        await confirm({
          variant: 'danger',
          title: 'Enable Data Retention Policy',
          description: `From this point forward, any inspection data created by this organization will be automatically and permanently deleted after ${
            values.retentionDays
          } day${values.retentionDays === 1 ? '' : 's'}. Existing data is not affected.`,
          acceptLabel: 'Enable',
          catchOnCancel: true,
        });
      } catch {
        return;
      }
    }

    const scimManaged = values.scimManaged === true;
    const scimChanged = Boolean(edit && scimManaged !== (initialValues.scimManaged === true));
    if (edit && initialValues.scimManaged === true && !scimManaged) {
      try {
        await confirm({
          variant: 'danger',
          title: 'Disable SCIM Management',
          description:
            "Are you sure you want to disable SCIM management? This will remove the organization's SCIM groups.",
          acceptLabel: 'Confirm',
          catchOnCancel: true,
        });
      } catch {
        actions.setFieldValue('scimManaged', initialValues.scimManaged === true);
        return;
      }
    }

    const organizationValues = { ...values };
    delete organizationValues.scimManaged;
    const response =
      edit && orgId
        ? await dispatchRequest(updateOrganization(organizationValues, orgId, initialValues, !scimChanged))
        : await dispatchRequest(createOrganization(organizationValues));
    if (response) {
      if (response.error === responseErrors.DOMAIN_ALREADY_EXISTS) {
        actions.validateForm({ ...values, domainInUse: true });
      } else if (response.error) {
        actions.resetForm();
      } else {
        if (scimChanged) {
          const targetId = edit || !response.data || !('id' in response.data) ? orgId : response.data.id;
          if (!targetId) {
            return;
          }
          const scimResponse = await dispatchRequest(setOrganizationScimManaged(targetId, scimManaged));
          if (!scimResponse || scimResponse.error) {
            if (!edit) {
              onClose();
            }
            return;
          }
        }
        onClose();
      }
    }
  };

  return (
    <VerifyDialog
      title={title}
      subTitle={subtitle}
      callToActionText=""
      iconType={iconType}
      open={open}
      isDivider
      handleCloseModal={onClose}
      hideCallToAction
      handleCallToActionClick={() => null}
      classes={{ paper: styles.paper }}
      contentClassName={styles.content}
    >
      <Formik
        key="settingsForm"
        validateOnBlur
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      >
        {(formikProps) => (
          <Form id="add-tenant-form" onSubmit={formikProps.handleSubmit} className={styles.form}>
            <GVTypography variant="subtitle1" className={styles.subtitle}>
              Tenant Info
            </GVTypography>
            <div className={styles.fields}>
              <div>
                <Field name="name">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      label="Organization Name"
                      id="name-tenant"
                      maxLength={46}
                    />
                  )}
                </Field>
              </div>
              <div>
                <Field name="customerId">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      // the best way I could find to limit the field to 6 digits
                      onValueChange={(value) => {
                        const digitString = (value ?? '').replace(/\D/, ''); // allow only digits
                        formikProps.setFieldValue('customerId', digitString, true);
                        formikProps.setFieldTouched('customerId', true, false);
                      }}
                      maxLength={6}
                      label="Customer ID"
                      id="customer-id-tenant"
                      isDisabled={edit}
                      className={styles.field}
                    />
                  )}
                </Field>
              </div>
              <div className={styles.full}>
                <Field name="veevaUrl">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      label="Veeva Integration URL"
                      id="veeva-integration-url-tenant"
                    />
                  )}
                </Field>
              </div>
              <div>
                <Field name="adminFirstName">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      label="Admin First Name"
                      id="admin-first-name-tenant"
                      isDisabled={edit}
                      className={styles.field}
                    />
                  )}
                </Field>
              </div>
              <div>
                <Field name="adminLastName">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      id="admin-last-name-tenant"
                      label="Admin Last Name"
                      isDisabled={edit}
                      className={styles.field}
                    />
                  )}
                </Field>
              </div>
              <div className={styles.full}>
                <Field name="adminEmail">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      id="admin-email-tenant"
                      label="Admin Email"
                      isDisabled={edit}
                      className={styles.field}
                    />
                  )}
                </Field>
              </div>
            </div>
            <div className={styles.rule}>
              <Divider light />
            </div>
            <GVTypography variant="subtitle1" className={styles.subtitle}>
              User Licence Plan
            </GVTypography>
            <div className={styles.fields}>
              <div>
                <Field name="seatsLimit">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) =>
                        fieldProps.form.setFieldValue(
                          fieldProps.field.name,
                          value == null || value === '' ? '' : Number(value),
                        )
                      }
                      id="seats-tenant"
                      label="# of seats"
                      type="number"
                      min={1}
                    >
                      <UIField.Steppers />
                    </GVTextField>
                  )}
                </Field>
              </div>
              <div>
                {/* This date picker needs quite some work,
                1: not being able to click outside of date picker to blur (1 billion error messages)
                2. initial values seems to break validation
                3. unparsible date values
                */}
                <DatePickerField name="expiry" testId="expiry-tenant" label="End Date" />
              </div>
              <div className={`${styles.full} ${styles.switches}`}>
                <div className={styles.switchRow}>
                  <Field name="eskoIntegration">
                    {(fieldProps: FieldProps) => (
                      <GVSwitch
                        name={fieldProps.field.name}
                        checked={fieldProps.field.value}
                        onChange={fieldProps.field.onChange}
                        id="esko-integration-tenant"
                      />
                    )}
                  </Field>
                  <GVTypography variant="subtitle1" display="inline">
                    Enable Esko Integration
                  </GVTypography>
                </div>
                <div className={styles.switchRow}>
                  <Field name="ssoEnabled">
                    {(fieldProps: FieldProps) => (
                      <GVSwitch
                        name={fieldProps.field.name}
                        checked={fieldProps.field.value}
                        onChange={fieldProps.field.onChange}
                        disabled={edit && orgInfo?.ssoEnabled}
                        id="sso-enabled-tenant"
                      />
                    )}
                  </Field>
                  <GVTypography variant="subtitle1" display="inline">
                    Enable SSO (Enterprise Only)
                  </GVTypography>
                </div>
                <div className={styles.switchRow}>
                  <Field name="hardSeatEnabled">
                    {(fieldProps: FieldProps) => (
                      <GVSwitch
                        name={fieldProps.field.name}
                        checked={fieldProps.field.value}
                        onChange={fieldProps.field.onChange}
                        id="hardSeat-enabled-tenant"
                      />
                    )}
                  </Field>
                  <GVTypography variant="subtitle1" display="inline">
                    Hard Seat Limit
                  </GVTypography>
                </div>
                <div className={styles.switchRow}>
                  <Field name="slowTrackCustomer">
                    {(fieldProps: FieldProps) => (
                      <GVSwitch
                        name={fieldProps.field.name}
                        checked={fieldProps.field.value}
                        onChange={fieldProps.field.onChange}
                        id="slow-track-customer-tenant"
                      />
                    )}
                  </Field>
                  <GVTypography variant="subtitle1" display="inline">
                    VALIDATE/SANDBOX Customer
                  </GVTypography>
                </div>
                <div className={styles.switchRow}>
                  <Field name="disableAiUsage">
                    {(fieldProps: FieldProps) => (
                      <GVSwitch
                        name={fieldProps.field.name}
                        checked={fieldProps.field.value}
                        onChange={fieldProps.field.onChange}
                        id="disable-ai-usage-tenant"
                      />
                    )}
                  </Field>
                  <GVTypography variant="subtitle1" display="inline">
                    Disable AI Usage
                  </GVTypography>
                </div>
                {edit && canManageScim && (
                  <div className={styles.switchRow}>
                    <Field name="scimManaged">
                      {(fieldProps: FieldProps) => (
                        <GVSwitch
                          name={fieldProps.field.name}
                          checked={fieldProps.field.value}
                          onChange={fieldProps.field.onChange}
                          id="scim-managed-tenant"
                        />
                      )}
                    </Field>
                    <GVTypography variant="subtitle1" display="inline">
                      SCIM Managed
                    </GVTypography>
                  </div>
                )}
              </div>
              {formikProps.values.ssoEnabled && (
                <div className={styles.full}>
                  <Field name="domain">
                    {(fieldProps: FieldProps) => (
                      <GVTextField
                        name={fieldProps.field.name}
                        hasError={!!fieldProps.meta.error}
                        message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                        value={fieldProps.field.value}
                        onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                        id="domain-tenant"
                        label="Domain"
                      />
                    )}
                  </Field>
                </div>
              )}
            </div>
            <div className={styles.rule}>
              <Divider light />
            </div>
            <GVTypography variant="subtitle1" className={styles.subtitle}>
              Data Retention
            </GVTypography>
            <div className={styles.fields}>
              <div>
                <GVSelect
                  id="retention-select"
                  label="Retention days"
                  value={formikProps.values.retentionDays == null ? null : String(formikProps.values.retentionDays)}
                  onValueChange={(nextValue) =>
                    formikProps.setFieldValue(
                      'retentionDays',
                      nextValue === SELECT_EMPTY_VALUE ? null : Number(nextValue),
                    )
                  }
                  options={RETENTION_OPTIONS.map((option) => ({
                    label: option.label,
                    value: option.value == null ? SELECT_EMPTY_VALUE : String(option.value),
                  }))}
                />
              </div>
            </div>

            <DialogActions className={styles.actions}>
              <Button color="inherit" id="add-tenant-form-cancel-button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                id="dialog-ok"
                disabled={!formikProps.isValid || !formikProps.dirty}
                type="submit"
                color="secondary"
                form="add-tenant-form"
                variant="contained"
              >
                {edit ? 'Save' : 'Send Invite'}
                {formikProps.isSubmitting && <CircularProgress className={styles.saving} size={16} color="primary" />}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </VerifyDialog>
  );
};

export default OrganizationModal;
